import { useCallback, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "drivenow_chat_session";

function readSessionId() {
  try {
    return sessionStorage.getItem(SESSION_KEY) || "";
  } catch {
    return "";
  }
}

function writeSessionId(id) {
  try {
    if (id) sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    /* ignore */
  }
}

export function useChatStream({ language, pageUrl, vehicleId }) {
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [waking, setWaking] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const wakingTimer = useRef(null);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setStreaming(false);
    setWaking(false);
    if (wakingTimer.current) {
      clearTimeout(wakingTimer.current);
      wakingTimer.current = null;
    }
  }, []);

  const send = useCallback(
    async (text) => {
      const content = (text || "").trim();
      if (!content || streaming) return;
      const history = [...messages, { role: "user", content }];
      setMessages([...history, { role: "assistant", content: "" }]);
      setStreaming(true);
      setError(null);
      setWaking(false);
      wakingTimer.current = setTimeout(() => setWaking(true), 8000);

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const response = await fetch(`${API}/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: history.slice(-16),
            session_id: readSessionId() || undefined,
            page_url: pageUrl,
            vehicle_id: vehicleId || undefined,
            language,
          }),
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          throw new Error("stream_failed");
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data:")) continue;
            let payload;
            try {
              payload = JSON.parse(line.slice(5).trim());
            } catch {
              continue;
            }
            if (payload.token) {
              setWaking(false);
              if (wakingTimer.current) {
                clearTimeout(wakingTimer.current);
                wakingTimer.current = null;
              }
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.role === "assistant") {
                  next[next.length - 1] = { ...last, content: (last.content || "") + payload.token };
                }
                return next;
              });
            }
            if (payload.session_id) writeSessionId(payload.session_id);
            if (payload.error) setError(payload.error);
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") setError("stream_failed");
      } finally {
        stop();
      }
    },
    [language, messages, pageUrl, streaming, stop, vehicleId]
  );

  const checkHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/health`, { timeout: 5000 });
      return Boolean(data && data.ok && data.configured !== false);
    } catch {
      return false;
    }
  }, []);

  const submitLead = useCallback(async (fields) => {
    await axios.post(`${API}/chat/lead`, {
      ...fields,
      session_id: readSessionId() || undefined,
      page_url: pageUrl,
    });
  }, [pageUrl]);

  return { messages, streaming, waking, error, send, stop, checkHealth, submitLead, setError };
}
